/**
 * Avatar — Renders a user's profile photo (presigned via AppSync) or a
 * styled initial fallback.
 *
 * Design notes (2026-05-22 simplification)
 * ----------------------------------------
 * The previous implementation had layered complexity that made the
 * "avatars never paint" bug on the admin User Management page hard to
 * pin down: dead thumbnail-fallback code, sessionStorage hydration of
 * an in-memory URL cache, a one-shot 403 refetch with a guard ref, and
 * a thumbnail-image error handler that could race with the URL cache.
 *
 * This rewrite keeps:
 *   - Module-level URL cache with a 12-minute serving window (S3
 *     presigns are 15-min; we leave a 3-min buffer).
 *   - In-flight request deduplication (rendering a list of 50 admins
 *     still fires one fetch per unique key).
 *   - Auth fall-through: try Cognito user pool first, fall back to API
 *     key for unauthenticated public pages (Contestants).
 *   - Optional `directUrl` bypass for callers that already have a URL
 *     (e.g. cropper preview).
 *
 * This rewrite drops:
 *   - sessionStorage hydration (was an attractive optimization but
 *     served stale URLs across tab reloads, causing the loader to flash
 *     then jump to initials when the cached URL had quietly expired).
 *   - Thumbnail-key fallback (no thumbnails are generated server-side
 *     today — `useThumbnail = false` was already set, but the dead
 *     branches confused readers and added retry races).
 *   - The 403-retry-with-evict ref (S3 expiry edge cases now resolve
 *     by waiting for the next render — Avatar re-fetches when the
 *     cache says the entry is stale, no special-case path).
 *
 * Contract:
 *   - Render the fallback initial immediately when there's no key.
 *   - When a key is present, show CrownLoader at the avatar's size
 *     while fetching, then crossfade to the photo.
 *   - On any failure, fall back to the initial fallback. Do not loop.
 */

import { useState, useEffect, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import { getProfilePhotoDownloadUrl } from '@/graphql/queries';
import CrownLoader from '@/components/ui/CrownLoader';

// ----------------------------------------------------------------------------
// URL cache + presign fetch
// ----------------------------------------------------------------------------

interface CachedUrl {
  url: string;
  fetchedAt: number;
}

/**
 * Module-level cache: s3Key -> presigned URL + the timestamp we minted it.
 *
 * S3 signs URLs with a 15-minute expiry. We serve from cache for up to
 * 12 minutes so the browser has 3 min of headroom for the actual image
 * fetch + clock skew. Past 12 min we evict and refetch.
 */
const urlCache = new Map<string, CachedUrl>();
const SERVE_MAX_AGE_MS = 12 * 60 * 1000;

/** Returns the cached URL if still inside the serving window, else null + evicts. */
function getCachedUrl(s3Key: string): string | null {
  const entry = urlCache.get(s3Key);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > SERVE_MAX_AGE_MS) {
    urlCache.delete(s3Key);
    return null;
  }
  return entry.url;
}

/** In-flight request dedup so a list of 50 rows only fires one fetch per unique key. */
const inflight = new Map<string, Promise<string | null>>();

// Lazy Amplify client — `generateClient()` at module load time can race
// the Amplify configure call. Typed as `any` because the precise return
// type of `generateClient()` triggers a TS "excessive stack depth"
// warning at this call site (the generic recursion in Amplify v6's
// model-shape inference is not relevant here — we only call `.graphql()`).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _client: any = null;
function getClient() {
  if (!_client) _client = generateClient();
  return _client;
}

/**
 * Calls the AppSync `getProfilePhotoDownloadUrl` resolver. Tries the
 * Cognito user-pool auth mode first (most call sites are post-auth);
 * falls back to API key for public pages where the user isn't signed
 * in. Returns null on any failure — callers render the initials
 * fallback.
 */
async function fetchPresignedUrl(s3Key: string): Promise<string | null> {
  const cached = getCachedUrl(s3Key);
  if (cached) return cached;

  const existing = inflight.get(s3Key);
  if (existing) return existing;

  const promise = (async (): Promise<string | null> => {
    try {
      // Try userPool first. If unauthenticated, this throws with
      // `NoUserPool` and we fall through to apiKey.
      let url: string | null = null;
      try {
        const res = (await getClient().graphql({
          query: getProfilePhotoDownloadUrl,
          variables: { s3Key },
          authMode: 'userPool',
        })) as GraphQLResult<{ getProfilePhotoDownloadUrl: string }>;
        url = res.data?.getProfilePhotoDownloadUrl ?? null;
      } catch {
        url = null;
      }
      if (!url) {
        try {
          const res = (await getClient().graphql({
            query: getProfilePhotoDownloadUrl,
            variables: { s3Key },
            authMode: 'apiKey',
          })) as GraphQLResult<{ getProfilePhotoDownloadUrl: string }>;
          url = res.data?.getProfilePhotoDownloadUrl ?? null;
        } catch {
          url = null;
        }
      }
      if (url) {
        urlCache.set(s3Key, { url, fetchedAt: Date.now() });
      }
      return url;
    } finally {
      inflight.delete(s3Key);
    }
  })();

  inflight.set(s3Key, promise);
  return promise;
}

/**
 * Prefetch presigned URLs for a batch of S3 keys. Call this early
 * (after fetching a list of users / contestants) to warm the cache so
 * Avatars paint without a per-row loader flash. Fire-and-forget — any
 * errors fall back to per-Avatar fetches at render time.
 */
export function prefetchAvatarUrls(s3Keys: string[]): void {
  for (const key of s3Keys) {
    if (!key) continue;
    if (getCachedUrl(key)) continue;
    if (inflight.has(key)) continue;
    void fetchPresignedUrl(key);
  }
}

// ----------------------------------------------------------------------------
// Validation helper exported for the upload form
// ----------------------------------------------------------------------------

/** Validates that an upload's file size is ≤ 5 MB. */
export function validateFileSize(sizeInBytes: number): boolean {
  return sizeInBytes <= 5_242_880;
}

// ----------------------------------------------------------------------------
// Public component
// ----------------------------------------------------------------------------

export interface LegacyAvatarProps {
  /** S3 key for the cropped avatar (preferred over profilePhotoS3Key). */
  croppedPhotoS3Key?: string | null;
  /** S3 key for the profile photo (optional). */
  profilePhotoS3Key?: string | null;
  /** Direct URL to display (bypasses S3 key lookup). */
  directUrl?: string | null;
  /** User's full name for initial fallback. */
  fullName: string;
  /** Size variant. */
  size: 'sm' | 'md' | 'lg';
  /** Color variant for the initial fallback. */
  variant?: 'contestant' | 'admin' | 'neutral';
  /** Border radius variant: 'full' for circular, 'rounded' for rounded-lg square. */
  shape?: 'full' | 'rounded';
  /** Additional CSS classes. */
  className?: string;
}

const sizeClasses: Record<LegacyAvatarProps['size'], string> = {
  sm: 'w-8 h-8 min-w-[2rem]',
  md: 'w-10 h-10 min-w-[2.5rem]',
  lg: 'w-32 h-32 min-w-[8rem]',
};
const sizeTextClasses: Record<LegacyAvatarProps['size'], string> = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-4xl',
};
const variantClasses: Record<NonNullable<LegacyAvatarProps['variant']>, string> = {
  contestant: 'bg-muted text-muted-foreground',
  admin: 'bg-primary text-primary-foreground',
  neutral: 'bg-muted text-muted-foreground',
};
const crownSizeMap: Record<LegacyAvatarProps['size'], number> = { sm: 20, md: 24, lg: 64 };

/** First character of fullName, uppercased. Returns "?" for empty input. */
function getInitial(fullName: string): string {
  const trimmed = fullName.trim();
  if (trimmed.length === 0) return '?';
  return trimmed.charAt(0).toUpperCase();
}

export function LegacyAvatar({
  croppedPhotoS3Key,
  profilePhotoS3Key,
  directUrl,
  fullName,
  size,
  variant = 'neutral',
  shape = 'rounded',
  className = '',
}: LegacyAvatarProps) {
  // Display priority:
  //   1. directUrl present                      → show that URL.
  //   2. croppedPhotoS3Key present              → fetch + show cropped avatar.
  //   3. profilePhotoS3Key present              → fetch + show original photo.
  //   4. neither present                        → show initial fallback.
  //
  // We resolve the effective S3 key once: prefer cropped over original.
  // We model these with a single `photoUrl` state and a `loadFailed`
  // flag. `loading` is implicit: photoUrl == null && have-a-key &&
  // !loadFailed.

  const effectiveS3Key = croppedPhotoS3Key || profilePhotoS3Key || null;

  const [photoUrl, setPhotoUrl] = useState<string | null>(() => {
    if (directUrl) return directUrl;
    if (effectiveS3Key) return getCachedUrl(effectiveS3Key);
    return null;
  });
  const [loadFailed, setLoadFailed] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Reset loaded state when the displayed URL changes so the
  // initials-shown-while-image-loads UX behaves correctly across props
  // changes (e.g. row re-render with a different user).
  useEffect(() => {
    setImgLoaded(false);
  }, [photoUrl]);

  // Sync directUrl prop changes — bypass the fetch path entirely.
  useEffect(() => {
    if (directUrl) {
      setPhotoUrl(directUrl);
      setLoadFailed(false);
    }
  }, [directUrl]);

  // Fetch presigned URL for an s3Key. The fetchPresignedUrl helper
  // returns immediately on a cache hit, so this effect is cheap to
  // re-run.
  const fetchTokenRef = useRef(0);
  useEffect(() => {
    if (directUrl) return;
    if (!effectiveS3Key) {
      setPhotoUrl(null);
      setLoadFailed(false);
      return;
    }
    // Fast path: cache hit.
    const cached = getCachedUrl(effectiveS3Key);
    if (cached) {
      setPhotoUrl(cached);
      setLoadFailed(false);
      return;
    }
    // Slow path: fetch from AppSync.
    setPhotoUrl(null);
    setLoadFailed(false);
    const token = ++fetchTokenRef.current;
    void (async () => {
      const url = await fetchPresignedUrl(effectiveS3Key);
      if (token !== fetchTokenRef.current) return; // stale
      if (url) {
        setPhotoUrl(url);
      } else {
        setLoadFailed(true);
      }
    })();
  }, [effectiveS3Key, directUrl]);

  const sizeClass = sizeClasses[size];
  const textClass = sizeTextClasses[size];
  const colorClass = variantClasses[variant];
  const radiusClass = shape === 'rounded' ? 'rounded-lg' : 'rounded-full';
  const initial = getInitial(fullName);

  const havePhoto = (directUrl || (effectiveS3Key && photoUrl)) && !loadFailed;
  const isLoading =
    !directUrl && !!effectiveS3Key && !photoUrl && !loadFailed;

  if (isLoading) {
    return (
      <div
        data-testid="avatar-loading"
        className={`${sizeClass} ${radiusClass} flex items-center justify-center ${colorClass} ${className}`.trim()}
      >
        <CrownLoader size={crownSizeMap[size]} color="#c9a84c" />
      </div>
    );
  }

  if (havePhoto) {
    return (
      <div className={`${sizeClass} ${radiusClass} relative overflow-hidden ${className}`.trim()}>
        {/* Initials shown until image fully decodes. */}
        {!imgLoaded && (
          <div
            className={`absolute inset-0 ${radiusClass} flex items-center justify-center font-semibold ${textClass} ${colorClass}`}
          >
            {initial}
          </div>
        )}
        <img
          data-testid="avatar-img"
          src={photoUrl!}
          alt={`${fullName || 'User'} avatar`}
          className={`w-full h-full object-cover object-top transition-opacity duration-200 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImgLoaded(true)}
          onError={() => {
            // S3 presign expired or cache served a stale URL. Evict
            // the entry so a re-render fetches a fresh one. We don't
            // refetch here — the next mount / prop change will pick
            // it up. If the image fails again after a fresh URL, we
            // surface as initials and stop trying. No retry loop.
            if (effectiveS3Key) urlCache.delete(effectiveS3Key);
            setLoadFailed(true);
          }}
        />
      </div>
    );
  }

  return (
    <div
      data-testid="avatar-initial"
      className={`${sizeClass} ${radiusClass} flex items-center justify-center font-semibold ${textClass} ${colorClass} ${className}`.trim()}
    >
      {initial}
    </div>
  );
}
